'use client';

import React from 'react';
import Image from 'next/image';
import type { GuestReview, OriginalLanguage } from '@/content/reviews';
import styles from './ReviewCards.module.css';

interface GuestReviewCardProps {
  review: GuestReview;
  currentLocale: 'en' | 'nl' | 'de';
  translations: {
    translatedFrom: string;
    languages: {
      de: string;
      nl: string;
      en: string;
    };
  };
}

// Color mapping for profile letters
const getProfileColor = (letter: string): string => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788',
    '#E07A5F', '#81B29A', '#F2CC8F', '#6A4C93', '#D4A5A5',
    '#9B59B6', '#3498DB', '#E74C3C', '#2ECC71', '#F39C12',
    '#1ABC9C', '#34495E', '#E67E22', '#95A5A6', '#16A085',
    '#27AE60',
  ];
  const index = letter.toUpperCase().charCodeAt(0) - 65;
  return colors[index % colors.length];
};

// Flag component with SVG images (works on all platforms including Windows)
const Flag: React.FC<{ lang: OriginalLanguage }> = ({ lang }) => {
  const flags = {
    de: '/images/flags/de.svg',
    nl: '/images/flags/nl.svg',
    en: '/images/flags/gb.svg',
  };
  return (
    <span className={styles.flagWrapper}>
      <Image
        src={flags[lang]}
        alt={lang}
        width={16}
        height={12}
        className={styles.flag}
      />
    </span>
  );
};

export function GuestReviewCard({ review, currentLocale, translations }: GuestReviewCardProps) {
  const reviewText = review[`review_${currentLocale}` as keyof GuestReview] as string;
  const showTranslation = review.originalLanguage !== currentLocale;

  return (
    <div className={styles.guestCard}>
      <div className={styles.guestHeader}>
        <div
          className={styles.profileCircle}
          style={{ backgroundColor: getProfileColor(review.profileLetter) }}
        >
          {review.profileLetter}
        </div>
        <div className={styles.guestInfo}>
          <div className={styles.guestName}>{review.name}</div>
          <div className={styles.guestMeta}>
            {review.date}
          </div>
        </div>
      </div>

      <div className={styles.rating}>
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={i < review.rating ? styles.starFilled : styles.starEmpty}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        ))}
      </div>

      <p className={styles.reviewText}>{reviewText}</p>

      {showTranslation && (
        <div className={styles.translationBadge}>
          <Flag lang={review.originalLanguage} />
          <span>
            {translations.translatedFrom} {translations.languages[review.originalLanguage]}
          </span>
        </div>
      )}

      <div className={styles.googleBadge}>
        <svg width="16" height="16" viewBox="0 0 48 48" fill="none">
          <path
            d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            fill="#FFC107"
          />
          <path
            d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            fill="#FF3D00"
          />
          <path
            d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            fill="#4CAF50"
          />
          <path
            d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            fill="#1976D2"
          />
        </svg>
        <span>{review.source}</span>
      </div>
    </div>
  );
}

